import memoize from "memoizee";
import getUserLocale from "get-user-locale";

import { getProxiedContainerHostname } from "../services/env";
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  DEFAULT_COUNTRY,
  SUPPORTED_COUNTRIES,
} from "../constants";
import { countryData, CountryData } from "./countries";
import { Locale, SupportedLocale } from "../models";
import { CoreTranslations, coreTranslationsMap } from "./translations";

/**
 * Takes a given locale and tries to return a standard locale string.
 * This is mostly used to turn lowercased locale strings ("en-us") to
 * their standard forms ("en-US".)
 *
 * If given just the language, return it lowercased.
 */
export const standardizeLocale = (locale: string): string => {
  const [language, territory] = locale.split("-");

  return territory
    ? `${language.toLowerCase()}-${territory.toUpperCase()}`
    : language.toLowerCase();
};

/**
 * Return Core Translations for a given, supported locale
 */
export const getCoreTranslationsFor = (
  locale: SupportedLocale,
): CoreTranslations => coreTranslationsMap[standardizeLocale(locale)];

/**
 * Return some useful country information for the given locale. Throw a
 * shitfit if it is not in a standard format.
 */
export const getCountryDataFor = (locale: string): CountryData => {
  const fragments = locale.split("-");

  if (fragments.length === 1) {
    throw new Error(
      `Supplied locale (${locale}) does not contain a territory (e.g. the "US" in "en-US")`,
    );
  }

  const data = countryData[fragments[1].toUpperCase()];
  if (!data) {
    throw new Error(
      `Could not find data for country/territory ${fragments[1]}`,
    );
  }

  return data;
};

/**
 * Aliasing since I think it's
 *
 * (a) A better name to avoid confusion, and
 *
 * (b) Might allow us to do funky things with its return values later
 *     (if at all). Run it through the refinement function defined in
 *     this module defensively in case people (QA especially) get
 *     clever... 😏
 */
export const getBrowserLocale = (): string =>
  standardizeLocale(getUserLocale());

/**
 * Just returns the lowercased language the browser's locale is set to.
 */
export const getBrowserLanguage = (): string =>
  getBrowserLocale().split("-")[0];

/**
 * Returns the country the application is deployed in. NOTE: This has
 * *absolutely nothing* to do with the user's locale preferences or the
 * browser's locale setting!
 *
 * If you want to override this, you'll need to run a local container
 * with REACT_APP_SET_REGION_TO="br" (or similar) in your env
 */
export const getCountryFromUrl = (): string => {
  if (
    process.env.REACT_APP_SET_REGION_TO &&
    process.env.REACT_APP_SET_REGION_TO.length > 0
  ) {
    return process.env.REACT_APP_SET_REGION_TO;
  }
  const { hostname } = window.location;
  const hostnameFragments = hostname.split(".");

  if (hostname === "localhost") {
    // If we are developing locally, attempt to
    // return the country of the container we are proxying to.
    // Otherwise, return the DEFAULT_COUNTRY.
    const proxiedContainerHostname = getProxiedContainerHostname();
    if (proxiedContainerHostname) {
      const proxiedContainerHostnameFragments =
        proxiedContainerHostname.split(".");
      const countryFromProxiedContainerHostname =
        proxiedContainerHostnameFragments[0];
      if (
        countryFromProxiedContainerHostname &&
        (SUPPORTED_COUNTRIES as ReadonlyArray<string>).includes(
          countryFromProxiedContainerHostname,
        )
      ) {
        console.debug(
          "[@granular/fabric-core][getCountryFromUrl] countryFromProxiedContainerHostname",
          countryFromProxiedContainerHostname,
        );
        return countryFromProxiedContainerHostname;
      }
    }
    return DEFAULT_COUNTRY;
  }

  if (
    !(SUPPORTED_COUNTRIES as ReadonlyArray<string>).includes(
      hostnameFragments[0],
    )
  ) {
    return DEFAULT_COUNTRY;
  }

  return hostnameFragments[0];
};

/**
 * Computes the locale which we want our developers to use with their
 * preferred I18n library.
 *
 * Why compute anything? Because the logic explained here captures
 * product requirements. Example: "What language should the user see
 * when they've loaded an app, set their language prefs to Canadian
 * English, set their browsers to Spanish, and the User Service fails?"
 *
 * Note: We're being *VERY particular* and *VERY explicit* with types
 * here and I hope the casting is readable. Please improve and send MR
 * if you disagree.
 */
export const computeLocaleWith = memoize(
  (userPreferredLocale: string | null): Locale => {
    const browserLocale = getBrowserLocale();

    /**
     * Yes, this can be set to `DEFAULT_LOCALE` and we can eliminate a few
     * conditionals below. But this is simpler and more *readable*. Keep
     * things that way 💫
     */
    let computedLocale: SupportedLocale;

    /**
     * If the User's preferred locale is found, it most likely means that
     * the User Service call went through. So do two things:
     *
     * (1) Refine the User Service-supplied locale to a standard locale
     *     E.g. "pt-br" -> "pt-BR"
     *
     * (2) Use this refined locale for a defensive check in case the User
     *     Service provides a locale we don't support. Note that this is a
     *     *very* low-likelihood event. If the locale is not supported,
     *     just use the default locale.
     */
    if (userPreferredLocale) {
      const standardizedLocale = standardizeLocale(userPreferredLocale);

      if (
        (SUPPORTED_LOCALES as ReadonlyArray<string>).includes(
          standardizedLocale,
        )
      ) {
        computedLocale = standardizedLocale as SupportedLocale;
      } else {
        computedLocale = DEFAULT_LOCALE;
      }
    } else if (
      browserLocale &&
      (SUPPORTED_LOCALES as ReadonlyArray<string>).includes(browserLocale)
    ) {
      /**
       * If the user's locale is not found (or is unsupported), turn to the
       * browser's locale. If the browser is set to something we support,
       * use it.
       */
      computedLocale = browserLocale as SupportedLocale;
    } else {
      /**
       * All else failed. Just use the default locale so the user sees
       * something lol.
       */
      computedLocale = DEFAULT_LOCALE;
    }

    return {
      locale: computedLocale,
    };
  },
  {
    // Only memoize the last result. We want this to recompute
    // when the userPreferredLocale changes, but we don't want
    // to be re-running this for every instance of every hook that uses locale.
    max: 1,
    profileName: "[i18n][helpers] computeLocaleWith",
  },
);

/**
 * Making "region" synonymous with "country" because quite a few
 * services use the former term. This is because we just took 'region'
 * from 'AWS Region' without bothering to think about what it really
 * means to us outside of AWS' context 🤷‍♂️✨
 *
 * For example, if we have customers in Argentina, we would presumably
 * have an https://ar.granular.ag which would be hosted in the `br` AWS
 * Region. Region and country are not synonymous. But we, at Granular,
 * mean "country" when we say "region." Lovely, no? Names matter.
 */
export const getRegionDataFor = getCountryDataFor;
export const getRegionFromUrl = getCountryFromUrl;
